import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import styled, { css, keyframes } from "styled-components";
import { motion, useAnimation, useMotionValue } from "framer-motion";

// Props型定義
export interface CircularFabProps {
  /** Material Symbolsアイコン名 */
  icon?: string;
  /** 回転するテキスト */
  text?: string;
  /** ON/OFF状態 */
  isActive?: boolean;
  /** クリックハンドラ */
  onClick?: () => void;
  /** 無効状態 */
  disabled?: boolean;
  /** 外径サイズ（px） */
  size?: number;
  /** 回転速度（秒/1周） */
  spinDuration?: number;
  /** マイクロインタラクションのテキスト（OFF→ON時に表示） */
  microInteractionText?: string;
  /** 追加CSSクラス */
  className?: string;
  /** クライアント / プロデューサーで表示を切り替える */
  variant?: "client" | "producer";
}

// マイクロインタラクション用wobbleアニメーション（free-sound-assetsと同じ）
const wobble = keyframes`
  0%, 100% {
    transform: translateX(-50%) translateX(0);
    transform-origin: 50% 50%;
  }
  15% {
    transform: translateX(-50%) translateX(-30px) rotate(-6deg);
  }
  30% {
    transform: translateX(-50%) translateX(15px) rotate(6deg);
  }
  45% {
    transform: translateX(-50%) translateX(-15px) rotate(-3.6deg);
  }
  60% {
    transform: translateX(-50%) translateX(9px) rotate(2.4deg);
  }
  75% {
    transform: translateX(-50%) translateX(-6px) rotate(-1.2deg);
  }
`;

// フェードアウト
const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

// Portal描画用ラッパー（FABと同じ位置・サイズをfixedで再現）
const MicroPortalWrapper = styled.div`
  position: fixed;
  pointer-events: none;
  z-index: 99999;
`;

// マイクロインタラクション用テキスト
const MicroText = styled.div<{ $duration: number }>`
  position: absolute;
  bottom: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 11px;
  color: #fff;
  background-color: #009ACE;
  padding: 2px 8px;
  border-radius: 12px;
  border: 1px solid #fff;
  display: flex;
  align-items: center;
  gap: 4px;
  animation:
    ${wobble} 0.8s ease-in-out both,
    ${fadeOut} 0.3s ease-out ${({ $duration }) => ($duration - 300)}ms forwards;

  & * {
    color: #fff !important;
  }
`;

// コンテナ
const FabContainer = styled.div<{
  $size: number;
  $isActive: boolean;
  $disabled: boolean;
  $variant: "client" | "producer";
}>`
  position: relative;
  z-index: 2;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  outline: none;
  border: none;
  background: none;
  padding: 0;

  /* 状態別の色管理（variant × isActive） */
  ${({ $isActive, $disabled, $variant }) => {
    if ($disabled) {
      return css`
        color: var(--soremo-border);
        --fab-icon-color: var(--soremo-border);
        --fab-text-color: var(--soremo-border);
      `;
    }

    if ($variant === "producer") {
      if ($isActive) {
        // プロデューサー ON: 青 → ホバーで deep-blue
        return css`
          color: var(--soremo-blue);
          --fab-icon-color: var(--soremo-blue);
          --fab-text-color: var(--soremo-blue);

          &:hover {
            --fab-icon-color: var(--soremo-deep-blue);
            --fab-text-color: var(--soremo-deep-blue);
          }
        `;
      }
      // プロデューサー OFF: アイコン soremo-border、テキストなし
      return css`
        color: var(--soremo-border);
        --fab-icon-color: var(--soremo-border);
        --fab-text-color: var(--soremo-border);
      `;
    }

    // --- client ---
    if ($isActive) {
      // クライアント ON: 青 → ホバーで deep-blue（テキストはホバー時のみ表示）
      return css`
        color: var(--soremo-blue);
        --fab-icon-color: var(--soremo-blue);
        --fab-text-color: var(--soremo-deep-blue);

        &:hover {
          --fab-icon-color: var(--soremo-deep-blue);
          --fab-text-color: var(--soremo-deep-blue);
        }
      `;
    }
    // クライアント OFF: light-gray → ホバーで青
    return css`
      color: var(--soremo-light-gray);
      --fab-icon-color: var(--soremo-light-gray);
      --fab-text-color: var(--soremo-light-gray);

      &:hover {
        --fab-icon-color: var(--soremo-blue);
        --fab-text-color: var(--soremo-blue);
      }
    `;
  }}
`;

// アイコン（40px固定）
const IconWrapper = styled.span`
  font-size: 40px;
  color: var(--fab-icon-color);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition: color 0.3s;
  line-height: 1;
  position: absolute;
`;

// 回転テキストラッパー（framer-motion）
const RotatingTextContainer = styled(motion.div)<{ $size: number }>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  text-align: center;
  transform-origin: 50% 50%;
`;

// 個々の文字
const CharSpan = styled.span<{ $fontSize: number }>`
  position: absolute;
  display: inline-block;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  font-size: ${({ $fontSize }) => $fontSize}px;
  color: var(--fab-text-color);
  transition: color 0.3s;
`;

// 回転トランジション生成
const getRotationTransition = (duration: number, from: number, loop = true) => ({
  from,
  to: from + 360,
  ease: "linear" as const,
  duration,
  type: "tween" as const,
  repeat: loop ? Infinity : 0,
});

const getTransition = (duration: number, from: number) => ({
  rotate: getRotationTransition(duration, from),
  scale: {
    type: "spring" as const,
    damping: 20,
    stiffness: 300,
  },
});

/**
 * CircularFab - 回転テキスト付きFABコンポーネント
 *
 * 中央に大きなアイコン、その周囲をテキストが円形に回転する。
 * ホバーで回転速度アップ。検収ボタンなどのトグルアクションに利用。
 */
const CircularFab: React.FC<CircularFabProps> = ({
  icon = "check_circle",
  text = "とりあえずOK",
  isActive = false,
  onClick,
  disabled = false,
  size = 80,
  spinDuration = 24,
  microInteractionText = "ありがとうございます！！",
  className,
  variant = "client",
}) => {
  const controls = useAnimation();
  const rotation = useMotionValue(0);
  const [showMicro, setShowMicro] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const prevIsActive = useRef(isActive);
  const fabRef = useRef<HTMLDivElement>(null);
  const [microRect, setMicroRect] = useState<DOMRect | null>(null);

  // variant × isActive × isHovered で表示テキストを決定
  const displayText = useMemo(() => {
    if (variant === "producer") {
      if (!isActive) return null; // プロデューサー OFF: テキストなし
      return isHovered ? "設定をリセット" : "";
    }
    // client
    if (isActive) return isHovered ? "設定をリセット" : "OKありがとう！";
    return text; // クライアント OFF: デフォルトテキスト
  }, [variant, isActive, isHovered, text]);

  // テキストを区切り文字付きで円形に配置
  const separator = " ";
  const fullText = displayText
    ? displayText + separator + displayText + separator
    : "";
  const letters = displayText ? Array.from(fullText) : [];
  const fontSize = Math.max(7, Math.round(size * 0.08));

  // 通常回転を開始
  useEffect(() => {
    const start = rotation.get();
    controls.start({
      rotate: start + 360,
      scale: 1,
      transition: getTransition(spinDuration, start),
    });
  }, [spinDuration, displayText, controls, rotation]);

  // isActive が false -> true に変わった場合にマイクロインタラクション表示
  useEffect(() => {
    if (!prevIsActive.current && isActive && microInteractionText) {
      setShowMicro(true);
      const timer = setTimeout(() => setShowMicro(false), 1500);
      return () => clearTimeout(timer);
    }
    prevIsActive.current = isActive;
  }, [isActive, microInteractionText]);

  // FABの位置をportal描画用に同期的にキャプチャ
  useLayoutEffect(() => {
    if (showMicro && fabRef.current) {
      setMicroRect(fabRef.current.getBoundingClientRect());
    } else {
      setMicroRect(null);
    }
  }, [showMicro]);

  // ホバー開始: 回転速度アップ + isHovered ON
  const handleHoverStart = useCallback(() => {
    setIsHovered(true);
    if (disabled) return;
    const start = rotation.get();
    controls.start({
      rotate: start + 360,
      scale: 1,
      transition: getTransition(spinDuration / 4, start),
    });
  }, [disabled, rotation, controls, spinDuration]);

  // ホバー終了: 通常速度に戻す + isHovered OFF
  const handleHoverEnd = useCallback(() => {
    setIsHovered(false);
    const start = rotation.get();
    controls.start({
      rotate: start + 360,
      scale: 1,
      transition: getTransition(spinDuration, start),
    });
  }, [rotation, controls, spinDuration]);

  const handleClick = useCallback(() => {
    if (disabled || !onClick) return;
    onClick();
  }, [disabled, onClick]);

  return (
    <>
      <FabContainer
        ref={fabRef}
        $size={size}
        $isActive={isActive}
        $disabled={disabled}
        $variant={variant}
        className={className}
        onClick={handleClick}
        onMouseEnter={handleHoverStart}
        onMouseLeave={handleHoverEnd}
        role="button"
        aria-label={isActive ? "検収済み" : "検収する"}
        aria-pressed={isActive}
      >
        {/* 回転テキスト（displayText が null の場合は非表示） */}
        {displayText && (
          <RotatingTextContainer
            $size={size}
            animate={controls}
            style={{ rotate: rotation }}
          >
            {letters.map((letter, i) => {
              const rotationDeg = (360 / letters.length) * i;
              return (
                <CharSpan
                  key={`${i}-${letter}`}
                  $fontSize={fontSize}
                  style={{
                    transform: `rotateZ(${rotationDeg}deg) translateY(-${Math.round(size * 0.12)}px)`,
                  }}
                >
                  {letter}
                </CharSpan>
              );
            })}
          </RotatingTextContainer>
        )}

        {/* 中央アイコン */}
        <IconWrapper
          className="material-symbols-rounded"
        >
          {icon}
        </IconWrapper>
      </FabContainer>

      {/* マイクロインタラクション（portalで最前面に描画） */}
      {showMicro && microRect && createPortal(
        <MicroPortalWrapper
          style={{
            top: microRect.top,
            left: microRect.left,
            width: microRect.width,
            height: microRect.height,
          }}
        >
          <MicroText $duration={1500}>
            {microInteractionText}
          </MicroText>
        </MicroPortalWrapper>,
        document.body
      )}
    </>
  );
};

export default React.memo(CircularFab);
